import React from 'react';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { formatMoneyAsUsdEstimate, DISPLAY_CURRENCY_USD } from '../../util/displayCurrency';
import { useDisplayCurrency } from '../../context/displayCurrencyContext';
import { propTypes } from '../../util/types';
import { resolveLatestProcessName, getProcess } from '../../transactions/transaction';

import css from './OrderBreakdown.module.css';

/**
 * A component that renders the total price as a line item.
 * Primary total stays in settlement currency (EUR). When the buyer prefers USD
 * and the transaction is still open (not completed/refunded), a secondary
 * estimate line is shown underneath.
 *
 * @component
 * @param {Object} props
 * @param {propTypes.transaction} props.transaction - The transaction to render
 * @param {boolean} props.isProvider - Whether the provider is the one receiving the commission
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
const LineItemTotalPrice = props => {
  const { transaction, isProvider, intl } = props;
  const { displayCurrency, eurToUsdRate } = useDisplayCurrency();
  const processName = resolveLatestProcessName(transaction?.attributes?.processName);
  if (!processName) {
    return null;
  }
  const process = getProcess(processName);
  const lastTransition = transaction?.attributes?.lastTransition;
  const isCompleted = process.isCompleted(lastTransition);
  const isRefunded = process.isRefunded(lastTransition);
  const showLiveUsdEstimate = !isProvider && !isCompleted && !isRefunded;

  let providerTotalMessageId = 'OrderBreakdown.providerTotalDefault';
  if (isCompleted) {
    providerTotalMessageId = 'OrderBreakdown.providerTotalReceived';
  } else if (isRefunded) {
    providerTotalMessageId = 'OrderBreakdown.providerTotalRefunded';
  }

  const totalLabel = isProvider ? (
    <FormattedMessage id={providerTotalMessageId} />
  ) : (
    <FormattedMessage id="OrderBreakdown.total" />
  );

  const totalPrice = isProvider
    ? transaction.attributes.payoutTotal
    : transaction.attributes.payinTotal;
  const formattedTotalPrice = formatMoney(intl, totalPrice);

  const usdEstimate =
    showLiveUsdEstimate && displayCurrency === DISPLAY_CURRENCY_USD
      ? formatMoneyAsUsdEstimate({ intl, money: totalPrice, eurToUsdRate })
      : null;

  return (
    <>
      <hr className={css.totalDivider} />
      <div className={css.lineItemTotal}>
        <div className={css.totalLabel}>{totalLabel}</div>
        <div className={css.totalPriceColumn}>
          <div className={css.totalPrice}>{formattedTotalPrice}</div>
          {usdEstimate ? (
            <div className={css.totalPriceEstimate}>
              <FormattedMessage
                id="OrderBreakdown.usdEstimate"
                values={{ amount: usdEstimate }}
              />
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default LineItemTotalPrice;
